import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle, FiInfo, FiX } from "react-icons/fi";

// ---- Button -------------------------------------------------------------------------

export function Button({ variant = "primary", size = "md", block = false, loading = false, icon = null, className = "", children, disabled, type = "button", ...rest }) {
  const classes = ["pa-btn", `pa-btn--${variant}`, `pa-btn--${size}`, block ? "pa-btn--block" : "", loading ? "pa-btn--loading" : "", className].filter(Boolean).join(" ");
  return (
    <button type={type} className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="pa-spinner pa-spinner--sm" aria-hidden="true" /> : icon}
      <span>{children}</span>
    </button>
  );
}

// ---- Card / Pill / Alert ---------------------------------------------------------------

export function Card({ className = "", padded = true, children, ...rest }) {
  return (
    <div className={["pa-card", padded ? "pa-card--padded" : "", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </div>
  );
}

export function Pill({ tone = "muted", children, className = "" }) {
  return <span className={`pa-pill pa-pill--${tone} ${className}`}>{children}</span>;
}

export function InlineAlert({ tone = "info", children, className = "" }) {
  const Icon = tone === "danger" ? FiAlertCircle : tone === "success" ? FiCheckCircle : FiInfo;
  return (
    <div className={`pa-alert pa-alert--${tone} ${className}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon className="pa-alert__icon" />
      <div>{children}</div>
    </div>
  );
}

export function SectionLabel({ children, right = null }) {
  return (
    <div className="pa-section-label">
      <span>{children}</span>
      {right ? <span className="pa-section-label__right">{right}</span> : null}
    </div>
  );
}

// ---- Segmented control ----------------------------------------------------------------

export function Segmented({ options, value, onChange, size = "md", className = "" }) {
  return (
    <div className={`pa-segmented pa-segmented--${size} ${className}`} role="radiogroup">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          className={`pa-segmented__item ${value === opt.value ? "is-active" : ""}`}
          onClick={() => onChange(opt.value)}
          disabled={opt.disabled}
          title={opt.title}
        >
          {opt.icon ? <span className="pa-segmented__icon">{opt.icon}</span> : null}
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  );
}

export function ChipGroup({ options, value, onChange, className = "" }) {
  return (
    <div className={`pa-chips ${className}`}>
      {options.map((opt) => (
        <button key={opt.value} type="button" className={`pa-chip ${value === opt.value ? "is-active" : ""}`} onClick={() => onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---- Form field --------------------------------------------------------------------------

export function Field({ label, hint, error, children, className = "" }) {
  return (
    <label className={`pa-field ${error ? "has-error" : ""} ${className}`}>
      {label ? <span className="pa-field__label">{label}</span> : null}
      {children}
      {error ? <span className="pa-field__error">{error}</span> : hint ? <span className="pa-field__hint">{hint}</span> : null}
    </label>
  );
}

// ---- Spinner / loading ---------------------------------------------------------------

export function Spinner({ size = "md", label = null }) {
  return (
    <div className="pa-loading">
      <span className={`pa-spinner pa-spinner--${size}`} aria-hidden="true" />
      {label ? <span className="pa-loading__label">{label}</span> : null}
    </div>
  );
}

export function EmptyState({ icon, title, text, action = null }) {
  return (
    <div className="pa-empty">
      {icon ? <div className="pa-empty__icon">{icon}</div> : null}
      <h3>{title}</h3>
      {text ? <p>{text}</p> : null}
      {action}
    </div>
  );
}

// ---- Stepper -------------------------------------------------------------------------------

export function Stepper({ steps, current }) {
  return (
    <ol className="pa-stepper" aria-label="Progress">
      {steps.map((step, i) => (
        <li key={step} className={`pa-stepper__step ${i < current ? "is-done" : ""} ${i === current ? "is-current" : ""}`}>
          <span className="pa-stepper__dot">{i < current ? "✓" : i + 1}</span>
          <span className="pa-stepper__label">{step}</span>
        </li>
      ))}
    </ol>
  );
}

// ---- Sheet (bottom sheet on phones, dialog on desktop) --------------------------------

export function Sheet({ open, onClose, title, children, footer = null, size = "md" }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="pa-sheet-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className={`pa-sheet pa-sheet--${size}`} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
        <div className="pa-sheet__handle" />
        {title ? (
          <div className="pa-sheet__header">
            <h3>{title}</h3>
            {onClose ? (
              <button type="button" className="pa-icon-btn" onClick={onClose} aria-label="Close">
                <FiX />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="pa-sheet__body">{children}</div>
        {footer ? <div className="pa-sheet__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

// ---- Toasts ---------------------------------------------------------------------------------

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);
  const remove = useCallback((id) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (message, { tone = "info", duration = 3500 } = {}) => {
      const id = (counter.current += 1);
      setToasts((prev) => [...prev.slice(-3), { id, message, tone }]);
      if (duration > 0) setTimeout(() => remove(id), duration);
      return id;
    },
    [remove]
  );
  const api = useMemo(
    () => ({
      show,
      success: (m, o) => show(m, { tone: "success", ...(o || {}) }),
      error: (m, o) => show(m, { tone: "danger", duration: 5000, ...(o || {}) }),
      info: (m, o) => show(m, { tone: "info", ...(o || {}) }),
    }),
    [show]
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pa-toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`pa-toast pa-toast--${t.tone}`} onClick={() => remove(t.id)}>
            {t.tone === "success" ? <FiCheckCircle /> : t.tone === "danger" ? <FiAlertCircle /> : <FiInfo />}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  return ctx || { show: () => {}, success: () => {}, error: () => {}, info: () => {} };
}

// ---- Misc ------------------------------------------------------------------------------------

export function Row({ label, value, strong = false, tone = null, className = "" }) {
  return (
    <div className={`pa-row ${strong ? "pa-row--strong" : ""} ${tone ? `pa-row--${tone}` : ""} ${className}`}>
      <span className="pa-row__label">{label}</span>
      <span className="pa-row__value">{value}</span>
    </div>
  );
}

export function Progress({ value, label = null }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className="pa-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="pa-progress__bar" style={{ width: `${pct}%` }} />
      {label ? <span className="pa-progress__label">{label}</span> : null}
    </div>
  );
}
